import React from 'react'
import { Link } from 'react-router-dom'
import { ROUTES } from '@/utils/constants'

interface CookiePolicyModalProps {
  isOpen: boolean
  onClose: () => void
}

const CookiePolicyModal: React.FC<CookiePolicyModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-900">Cookie Policy</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Divider */}
          <div className="border-b border-blue-200 mb-6"></div>

          {/* Content */}
          <div className="prose prose-gray max-w-none">
            <p className="text-sm text-gray-600 mb-6">
              <strong>Effective Date:</strong> September 22, 2025
            </p>

            <p className="text-gray-700 mb-6">
              <strong>⚠️ Scope:</strong> This Cookie Policy applies only to our corporate website. 
              It does not apply to cookies used in our SaaS platform, which are covered under our 
              <Link
                to={ROUTES.PLATFORM_PRIVACY}
                onClick={onClose}
                className="bg-yellow-200 px-1 rounded hover:bg-yellow-300 transition-colors underline"
              >
                Platform Privacy Policy and DPF Statement
              </Link>.
            </p>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">1. What Are Cookies?</h3>
              <p className="text-gray-700 mb-4">
                Small text files stored on your device. We may also use pixels, tags, and scripts.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">2. Types of Cookies</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
                <li><strong>Strictly Necessary</strong> (always active) - Required for website functionality</li>
                <li><strong>Performance & Analytics</strong> - Help us understand website usage</li>
                <li><strong>Functional</strong> - Remember your preferences and enhance features</li>
                <li><strong>Advertising/Targeting</strong> - Deliver relevant ads and measure effectiveness</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">3. How We Use Cookies</h3>
              <p className="text-gray-700 mb-4">
                For functionality, analytics, preferences, and marketing.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">4. Managing Cookies</h3>
              <p className="text-gray-700 mb-4">
                Visitors can accept, reject, or customize cookie use. Non-essential cookies require consent.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">5. Third-Party Cookies</h3>
              <p className="text-gray-700 mb-4">
                Some cookies are set by third parties like Google or LinkedIn.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">6. Updates</h3>
              <p className="text-gray-700 mb-4">
                We may update this policy periodically.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">7. Contact Us</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-700">
                  <a href="mailto:management@eceptionist.com" className="text-blue-600 underline hover:text-blue-800">management@eceptionist.com</a>
                </p>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-6 mt-8">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CookiePolicyModal